<template>
  <!-- Estilo unificado con UpdateStatusesModal de viewer-pixi:
       rounded-lg + border + shadow, HEADER con title+close, FOOTER con v-button.
       Se abre desde el Toolbar (botón "Procesar") en electrical/trenches. -->
  <b-modal :active.sync="isModalActive" :width="780" scroll="keep" :can-cancel="['escape','outside']">
    <div class="rounded-lg bg-white overflow-hidden border border-[#E4E6EB] shadow-lg">

      <!-- HEADER estilo Meta -->
      <header class="flex justify-between items-center px-4 py-3 border-b border-[#E4E6EB] bg-white">
        <div class="flex-1 min-w-0">
          <h2 class="text-base font-semibold text-gray-900 break-words whitespace-normal">
            {{ $t('select_layers_to_track') }}
          </h2>
        </div>
        <button
          @click="isModalActive = false"
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1"
          aria-label="Cerrar">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </header>

      <!-- TOOLBAR INTERNO: contador + toggle-all -->
      <div class="flex justify-between items-center px-4 py-2 bg-gray-50 border-b border-[#E4E6EB]">
        <span class="text-xs font-semibold text-gray-600 bg-gray-200 rounded-full px-3 py-1">
          {{ selectedCount }} / {{ rows.length }}
        </span>
        <b-button
          size="is-small"
          icon-left="check-double"
          icon-pack="fas"
          @click="_OnToggleAll">
          {{ allSelected ? $t('deselect_all') : $t('select_all') }}
        </b-button>
      </div>

      <!-- CONTENIDO -->
      <div class="bg-white">
        <div v-if="rows.length" class="select-layers__body">
          <table class="select-layers__table">
            <thead>
              <tr>
                <th style="width: 36px"></th>
                <th style="width: 22px"></th>
                <th>{{ $t('layer') }}</th>
                <th class="num" style="width: 90px">{{ $t('entities') }}</th>
                <th class="num" style="width: 110px">{{ $t('length_m') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.name"
                  :class="{ 'is-selected': row.selected, 'is-disabled': !row.hasLength }">
                <td>
                  <!-- Capas sin segmentos de longitud no son trackeables en
                       electrical/trenches → checkbox deshabilitado. -->
                  <b-checkbox v-model="row.selected" :disabled="!row.hasLength"
                    :title="row.hasLength ? '' : $t('layer_without_length_disabled')" />
                </td>
                <td>
                  <span class="swatch" :style="{ backgroundColor: _CssColor(row.color) }"></span>
                </td>
                <td class="name">
                  <!-- Renombrar bloqueado si la capa no es trackeable (sin longitud). -->
                  <input
                    v-model="row.displayName"
                    type="text"
                    class="select-layers__name-input"
                    :disabled="!row.hasLength"
                    :placeholder="row.name"
                    :title="row.hasLength ? $t('rename_layer_hint', [row.name]) : $t('layer_without_length_disabled')"
                  />
                </td>
                <td class="num">{{ row.entityCount }}</td>
                <td class="num">{{ _FormatLength(row.lengthMeters) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="select-layers__empty">
          {{ $t('no_layers_to_display') }}
        </div>
      </div>

      <!-- FOOTER estilo Meta -->
      <footer class="px-4 py-3 border-t border-[#E4E6EB] bg-white">
        <div class="w-full flex items-center justify-between gap-2">
          <v-button color="grey" icon="door-closed" size="small" @click="isModalActive = false">
            {{ $t('cancel') }}
          </v-button>
          <v-button
            icon="save"
            size="small"
            :disabled="selectedCount === 0"
            @click="_OnApply">
            {{ $t('save') }}
          </v-button>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "SelectLayersModal",

  data() {
    return {
      isModalActive: false,
      // Copia mutable de las capas que recibe en show(layers). El input v-model
      // de cada fila escribe en `displayName` y los checkbox en `selected`.
      // Mantener aquí (no en data del padre) permite que el usuario cancele y
      // descarte ediciones sin afectar la fuente original.
      rows: [],
    }
  },

  computed: {
    selectedCount() {
      return this.rows.filter(r => r.selected).length
    },
    /** Filas seleccionables (con segmentos de longitud). */
    selectableRows() {
      return this.rows.filter(r => r.hasLength)
    },
    allSelected() {
      return this.selectableRows.length > 0 && this.selectableRows.every(r => r.selected)
    },
  },

  methods: {
    /**
     * Inicializa el modal con las capas a mostrar.
     * @param {Array<{name, displayName, color, entityCount, lengthMeters, selected}>} layers
     */
    show(layers) {
      // Clonado superficial — el modal manda; las ediciones no afectan el original
      // hasta que el usuario haga Apply.
      this.rows = (layers || []).map(l => {
        // Sin segmentos de longitud (misma regla que la columna "—") → no es
        // trackeable: queda deseleccionada y bloqueada.
        const hasLength = (l.lengthMeters || 0) >= 0.01
        return {
          name:         l.name,
          displayName:  l.displayName || l.name,
          color:        l.color,
          entityCount:  l.entityCount,
          lengthMeters: l.lengthMeters,
          hasLength,
          selected:     hasLength ? (l.selected !== false) : false,
        }
      })
      this.isModalActive = true
    },

    hide() {
      this.isModalActive = false
      this.rows = []
    },

    _OnToggleAll() {
      // Solo afecta a las filas seleccionables; las bloqueadas (sin longitud)
      // se mantienen deseleccionadas.
      const next = !this.allSelected
      this.selectableRows.forEach(r => { r.selected = next })
    },

    _OnApply() {
      // Emitimos solo las capas seleccionadas con `name` (clave estable del DXF)
      // y `displayName` (lo que el usuario haya editado). El padre construye los
      // descriptores finales para savePictogram.
      const selected = this.rows
        .filter(r => r.selected)
        .map(r => ({
          name:        r.name,
          displayName: (r.displayName || '').trim() || r.name,
        }))
      this.$emit('epc-apply', selected)
      this.hide()
    },

    _CssColor(value) {
      let s = (value || 0).toString(16)
      while (s.length < 6) s = '0' + s
      return '#' + s
    },

    _FormatLength(meters) {
      if (!meters || meters < 0.01) return '—'
      return meters >= 100 ? meters.toFixed(0) : meters.toFixed(2)
    },
  },
}
</script>

<style scoped>
.select-layers__body {
  max-height: 50vh;
  overflow-y: auto;
}

.select-layers__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.select-layers__table th,
.select-layers__table td {
  padding: 6px 12px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
  vertical-align: middle;
}

.select-layers__table th {
  background: #fafafa;
  font-weight: 600;
  font-size: 12px;
  color: #555;
  position: sticky;
  top: 0;
  z-index: 1;
}

.select-layers__table td.name {
  max-width: 320px;
}

.select-layers__table th.num,
.select-layers__table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.select-layers__table tr.is-selected {
  background: rgba(85, 119, 187, 0.06);
}

/* Capa sin segmentos de longitud: no trackeable → atenuada y bloqueada. */
.select-layers__table tr.is-disabled {
  opacity: 0.55;
}

.select-layers__table tr.is-disabled .select-layers__name-input {
  cursor: not-allowed;
}

.swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid #ccc;
}

/* Input inline para renombrar la capa — estilo MD3 / click-to-edit
   (Linear/Notion). En reposo parece texto plano; en hover se sugiere la
   edición con un background sutil + outline muy ligero; en focus aparece
   un focus-ring azul de 3px con sombra suave. La transición es la misma
   en las tres props para que el cambio sea coherente. */
.select-layers__name-input {
  width: 100%;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  outline: none;
  cursor: text;
  transition:
    background-color 0.15s ease,
    border-color    0.15s ease,
    box-shadow      0.15s ease;
}

.select-layers__name-input:hover {
  background-color: #f3f4f6;
  border-color: #e5e7eb;
}

/* Focus: usa el azul de marca de la app ($primary: #5577BB) con focus-ring
   en primary-lighter de muy baja opacidad — sobrio, coherente con Buefy/Bulma
   pero sin el efecto eléctrico del cyan que usaba el toolbar legacy. */
.select-layers__name-input:focus {
  background-color: #ffffff;
  border-color: #5577BB;
  box-shadow: 0 0 0 3px rgba(85, 119, 187, 0.16);
}

.select-layers__name-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
  font-style: italic;
}

/* Input de renombrar bloqueado (capa sin longitud): no editable ni con hover. */
.select-layers__name-input:disabled {
  cursor: not-allowed;
  color: #9ca3af;
}

.select-layers__name-input:disabled:hover {
  background-color: transparent;
  border-color: transparent;
}

.select-layers__empty {
  text-align: center;
  padding: 24px;
  color: #888;
}
</style>
